import { useCallback } from 'react'
import { useIntegrations } from '../views/Integration/useIntegrations'
import { selectAccountId } from '../store/auth/authSlice'
import { useAppSelector } from '../store/hooks'
import { useGetMeQuery } from '../store/user/user'
import { IntegrationRequirementResult, INTEGRATIONS_REQUIRING_USER_CONNECTION } from './integrationRequirement'
import { IntegrationServiceId } from './integrations'

/**
 * Returns a checker that evaluates whether the current user satisfies the requirements to use a given integration provider.
 *
 * Requirement rules:
 * 1. Account must allow the integration (enterprise config)
 * 2. If the provider requires a user-level connection (Zoom):
 *    - User must have a 'live' integration
 */
export const useIntegrationRequirement = () => {
  const accountId = useAppSelector(selectAccountId)
  const { isIntegrationAllowed } = useIntegrations()
  const { data: currentUser } = useGetMeQuery({ accountId })

  return useCallback(
    (serviceId: IntegrationServiceId): IntegrationRequirementResult => {
      const accountAllowed = isIntegrationAllowed(serviceId)
      const hasLiveIntegration = currentUser?.integrations?.some((i) => i.serviceId === serviceId && i.state === 'live') ?? false
      const satisfied = accountAllowed ? hasLiveIntegration || !INTEGRATIONS_REQUIRING_USER_CONNECTION.has(serviceId) : false
      return { satisfied, accountAllowed }
    },
    [isIntegrationAllowed, currentUser],
  )
}
